<script setup lang="ts">
import { Check, X } from '@lucide/vue'
import type { Component } from 'vue'

/**
 * Coquille d'une étape du « poste de contrôle » PreLLM. Rail vertical à gauche
 * (icône dans une pastille + connecteur), contenu à droite. L'état pilote la
 * couleur et l'indicateur — c'est ce qui donne la sensation « ça se déclenche
 * en direct ».
 */
type Status = 'idle' | 'running' | 'done' | 'blocked' | 'skipped' | 'error'

defineProps<{
    index: number
    title: string
    caption?: string
    icon: Component
    status: Status
    last?: boolean
}>()
</script>

<template>
    <div :class="['stage', `stage--${status}`]">
        <div class="stage__rail">
            <span class="stage__node">
                <component :is="icon" :size="16" :stroke-width="2" />
                <span v-if="status === 'done'" class="stage__flag stage__flag--done">
                    <Check :size="11" :stroke-width="3" />
                </span>
                <span
                    v-else-if="status === 'blocked' || status === 'error'"
                    class="stage__flag stage__flag--blocked"
                >
                    <X :size="11" :stroke-width="3" />
                </span>
            </span>
            <span v-if="!last" class="stage__line"></span>
        </div>

        <div class="stage__body">
            <header class="stage__head">
                <div class="stage__titles">
                    <span class="stage__step">Étape {{ index }}</span>
                    <h3 class="stage__title">{{ title }}</h3>
                </div>
                <span class="stage__status">
                    <span v-if="status === 'running'" class="stage__pulse" aria-hidden="true"></span>
                    {{
                        status === 'running'
                            ? 'En cours'
                            : status === 'done'
                              ? 'OK'
                              : status === 'blocked'
                                ? 'Bloqué'
                                : status === 'error'
                                  ? 'Erreur'
                                  : status === 'skipped'
                                    ? '—'
                                    : 'En attente'
                    }}
                </span>
            </header>
            <p v-if="caption" class="stage__caption">{{ caption }}</p>
            <div v-if="status !== 'idle'" class="stage__content">
                <slot />
            </div>
        </div>
    </div>
</template>

<style scoped>
.stage {
    display: grid;
    grid-template-columns: 32px 1fr;
    gap: 14px;
}

/* === Rail === */
.stage__rail {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.stage__node {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--p-radius-full);
    border: 1px solid var(--p-color-line);
    background: var(--p-color-surface);
    color: var(--p-color-ink-4);
    flex-shrink: 0;
    transition:
        color var(--p-motion-base) var(--p-ease-out),
        border-color var(--p-motion-base) var(--p-ease-out),
        background var(--p-motion-base) var(--p-ease-out);
}
.stage__flag {
    position: absolute;
    right: -3px;
    bottom: -3px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    border-radius: var(--p-radius-full);
    border: 1.5px solid var(--p-color-surface);
    color: #fff;
}
.stage__flag--done {
    background: var(--p-color-euca);
}
.stage__flag--blocked {
    background: var(--p-color-brick);
}
.stage__line {
    flex: 1;
    width: 1px;
    margin: 4px 0;
    background: var(--p-color-line);
    min-height: 12px;
}

/* === Body === */
.stage__body {
    padding-bottom: 22px;
    min-width: 0;
}
.stage__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
}
.stage__titles {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}
.stage__step {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-5);
}
.stage__title {
    margin: 0;
    font-family: var(--p-font-display);
    font-size: var(--p-text-md);
    font-weight: var(--p-font-weight-medium);
    color: var(--p-color-ink-4);
    line-height: var(--p-line-snug);
    transition: color var(--p-motion-base) var(--p-ease-out);
}
.stage__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wide);
    color: var(--p-color-ink-5);
}
.stage__pulse {
    width: 6px;
    height: 6px;
    border-radius: var(--p-radius-full);
    background: var(--p-color-olive);
    animation: stage-pulse 1.4s ease-in-out infinite;
}
@keyframes stage-pulse {
    0%,
    100% {
        opacity: 0.35;
        transform: scale(0.85);
    }
    50% {
        opacity: 1;
        transform: scale(1);
    }
}
.stage__caption {
    margin: 6px 0 0;
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-5);
    line-height: var(--p-line-normal);
}
.stage__content {
    margin-top: 12px;
    animation: stage-reveal var(--p-motion-base) var(--p-ease-out);
}
@keyframes stage-reveal {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}

/* === States === */
.stage--running .stage__node {
    color: var(--p-color-olive);
    border-color: var(--p-color-olive);
    background: var(--p-color-olive-bg);
}
.stage--running .stage__title,
.stage--done .stage__title,
.stage--blocked .stage__title {
    color: var(--p-color-ink);
}
.stage--running .stage__status {
    color: var(--p-color-olive);
}
.stage--done .stage__node {
    color: var(--p-color-euca);
    border-color: var(--p-color-euca-soft);
    background: var(--p-color-euca-bg);
}
.stage--done .stage__status {
    color: var(--p-color-euca);
}
.stage--blocked .stage__node {
    color: var(--p-color-brick);
    border-color: var(--p-color-brick-soft);
    background: var(--p-color-brick-bg);
}
.stage--blocked .stage__status {
    color: var(--p-color-brick);
}
.stage--blocked .stage__line {
    background: var(--p-color-brick-soft);
}
.stage--error .stage__node {
    color: var(--p-color-amber);
    border-color: var(--p-color-amber-soft);
    background: var(--p-color-amber-bg);
}
.stage--error .stage__flag--blocked {
    background: var(--p-color-amber);
}
.stage--error .stage__title {
    color: var(--p-color-ink);
}
.stage--error .stage__status {
    color: var(--p-color-amber);
}
.stage--skipped {
    opacity: 0.55;
}
</style>
